import * as Sentry from "@sentry/browser";
import useScript from "core-integration/src/hooks/useScript";
import useScrollToTop from "core-integration/src/hooks/useScrollToTop";
import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getBootstrapped } from "core-integration/src/store/bootstrap/reducers";
import { bootstrap } from "core-integration/src/store/bootstrap/thunks";
import { getServerError } from "core-integration/src/store/global/reducers";
import { IServerError } from "core-integration/src/store/global/types";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Box } from "rebass/styled-components";
import styled, { ThemeProvider, css } from "styled-components";
import GlobalStyle from "../GlobalStyle";
import "../fonts.css";
import i18n from "../i18n";
import theme from "../theme";
import { SITE_IDS, getSite } from "../utils/sites";
import ElementDialog from "./ElementDialog";
import FixturesLoader from "./FixturesLoader";
import Footer from "./Footer";
import GameHeader from "./GameHeader";
import { Main, Wrapper } from "./Layout";
import ServerError from "./ServerError";
import AutoJoinDialog from "./leagues/AutoJoinDialog";

const Sport5 = React.lazy(() => import("./sites/sport5"));
const LequipeHeader = React.lazy(() => import("./sites/lequipe"));

interface IPropsHeaderWrapper {
  site: string;
}

const headerStyles = {
  euro: css`
    padding-top: 53px;

    @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
      padding-top: 70px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints[5]}) {
      padding-top: 163px;
    }
  `,
  lequipe: css`
    position: sticky;
    top: 0;
    z-index: ${({ theme }) => theme.zIndex.clientMenu};
  `,
};

interface IProps {
  bootstrapped: boolean;
  serverError: IServerError | null;
}

export const Loading = () => {
  const { t } = useTranslation();
  return (
    <Wrapper>
      <Main>
        <Box mx={2}>{t("app.loading", "Loading ...")}</Box>
      </Main>
    </Wrapper>
  );
};

const StyledHeaderWrapper = styled.div<IPropsHeaderWrapper>`
  ${({ site }) => site === "euro" && headerStyles.euro};
  ${({ site }) => site === "lequipe" && headerStyles.lequipe};
`;

const StyledFooterWrapper = styled.div``;

const App = ({ bootstrapped, serverError }: IProps) => {
  const site = getSite();
  // Handle scroll restoration
  useScrollToTop();

  const siteHeaders: Record<SITE_IDS, React.ReactNode> = {
    main_us: null,
    main_en: null,
    main_fr: null,
    main_es: null,
    main_it: null,
    main_de: null,
    main_el: null,
    main_he: null,
    main_br: null,
    main_tr: null,
    main_ar: null,
    sport5: <Sport5 />,
    lequipe: <LequipeHeader />,
  };
  const siteFooters: Record<SITE_IDS, React.ReactNode> = {
    main_us: null,
    main_en: null,
    main_fr: null,
    main_es: null,
    main_it: null,
    main_de: null,
    main_el: null,
    main_he: null,
    main_br: null,
    main_tr: null,
    sport5: null,
    main_ar: null,
    lequipe: null,
  };
  useScript(import.meta.env.VITE_WIDGET_SRC);

  return (
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <React.Suspense fallback={<div />}>
          <title>{i18n.t("app.gameName", "NBA Fantasy")}</title>
          <meta
            name="description"
            content={i18n.t<string>(
              "app.description1n",
              "The new NBA Fantasy game for the 2026/27 season. Build your dream roster of NBA players. Play with your friends and fans from around the world to be the NBA Fantasy Champion.",
            )}
          />
          <StyledHeaderWrapper site={site}>
            {siteHeaders[site]}
          </StyledHeaderWrapper>
          <GlobalStyle />
          <GameHeader site={site} />
          {serverError ? (
            <ServerError error={serverError} />
          ) : bootstrapped ? (
            <FixturesLoader />
          ) : (
            <Loading />
          )}
          <Footer />
          <StyledFooterWrapper>{siteFooters[site]}</StyledFooterWrapper>

          <AutoJoinDialog />
          <ElementDialog />
        </React.Suspense>
      </ThemeProvider>
    </React.StrictMode>
  );
};

interface IContainerPropsFromState {
  bootstrapped: boolean;
  serverError: IServerError | null;
}

interface IState {
  eventId: string | null;
  error: Error | null;
}

interface IContainerPropsFromDispatch {
  bootstrapApp: () => Promise<void>;
}

type ContainerProps = IContainerPropsFromState & IContainerPropsFromDispatch;

interface ErrorFallbackProps {
  eventId: string | null;
}

const ErrorFallback = ({ eventId }: ErrorFallbackProps) => {
  const { t } = useTranslation();
  return (
    <>
      <h2>{t("app.error", "Error")}</h2>
      <p>{t("app.wrong", "We're sorry - something's gone wrong.")}</p>
      {import.meta.env.VITE_SENTRY_ENVIRONMENT && (
        <>
          <p>
            {t(
              "app.notified",
              "Our team has been notified, but click here to fill out a report.",
            )}
          </p>
          <button
            onClick={() =>
              Sentry.showReportDialog({
                eventId: eventId as string,
              })
            }
          >
            {t("app.report", "Report feedback")}
          </button>
        </>
      )}
    </>
  );
};
class AppContainer extends React.Component<ContainerProps, IState> {
  public state: IState = { error: null, eventId: null };
  public componentDidMount() {
    this.props.bootstrapApp();
  }

  public componentDidCatch(error: Error | null, errorInfo: {}) {
    this.setState({ error });
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
      Sentry.withScope((scope) => {
        scope.setExtras(errorInfo);
        const eventId = Sentry.captureException(error);
        this.setState({ eventId });
      });
    }
  }

  public render() {
    // JS Error
    if (this.state.error) {
      // Can't reliably use any of our components, eg Button
      return <ErrorFallback eventId={this.state.eventId} />;
    }
    // All OK - Render App
    return (
      <App
        bootstrapped={this.props.bootstrapped}
        serverError={this.props.serverError}
      />
    );
  }
}

const mapStateToProps = (state: RootState): IContainerPropsFromState => ({
  bootstrapped: getBootstrapped(state),
  serverError: getServerError(state),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch,
): IContainerPropsFromDispatch => ({
  bootstrapApp: () => dispatch(bootstrap()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AppContainer);
